/*
 * Copyright (c) 2025 Bolmate B.V.
 */
import './style/currentSubscription.scss';

import {AwesomeIcon, ChangeFunction, ElementPosition, Link, Loader, OrnamentShape} from '@dvrd/dvr-controls';

import React, {CSSProperties, MouseEventHandler, useState} from 'react';

import {useTranslation} from 'react-i18next';

import Subscription from '../../../../models/subscriptionModel';

import {SubscriptionModuleName, SubscriptionModuleStatus} from '../../../../util/enums';

import {SubscriptionStatus} from '../../../../util/interfaces';

import {renderPlural} from '../../../../util/utils';

import {BolmateButton} from '../../../controls';

import Ideal from '../../../misc/ideal';

import Image from '../../../misc/image';

import Mastercard from '../../../misc/mastercard';

import Sepa from '../../../misc/sepa';

import BolmateSwitch from '../../../controls/switch/bolmateSwitch';

import {SubscriptionPricing} from '../../../../util/types';

import classNames from 'classnames';

interface Props {
    onClickFailedPayment: MouseEventHandler;
    onClickStart: MouseEventHandler;
    onClickPortal: MouseEventHandler;
    onToggleNotificationsModule: ChangeFunction<boolean>;
    subscription: Subscription;
    loading: boolean;
    extraAccounts: number;
    currentPricing: SubscriptionPricing;
    discountPrice: number | null;
    orderCount: number;
    usersEstimate: number;
}

export default function CurrentSubscription(props: Props) {
    const {t} = useTranslation();
    const {
        onClickFailedPayment,
        onClickStart,
        onClickPortal,
        onToggleNotificationsModule,
        subscription,
        loading,
        extraAccounts,
        discountPrice,
        orderCount,
        currentPricing,
        usersEstimate
    } = props;
    const [moduleOpen, setModuleOpen] = useState(false);

    function onToggleModuleOpen() {
        setModuleOpen(!moduleOpen);
    }

    function renderButton() {
        const {status, hasPaymentMethod, failedPayment} = subscription;
        if (status === SubscriptionStatus.FREE) return <div />;
        let btnAction: MouseEventHandler;
        let btnLabel: string;
        let ornament: OrnamentShape | undefined = undefined;
        if (failedPayment) {
            btnAction = onClickFailedPayment;
            btnLabel = t('settings.subscription.payOpenInvoice', 'Openstaande factuur betalen');
        } else if (status === SubscriptionStatus.CANCELED) {
            btnAction = onClickStart;
            btnLabel = t('settings.subscription.startSubscriptionAction', 'Abonnement starten');
            ornament = {element: <AwesomeIcon name='arrow-right' />, placement: ElementPosition.RIGHT};
        } else if (status === SubscriptionStatus.INACTIVE || !hasPaymentMethod) {
            btnAction = onClickStart;
            btnLabel = t('settings.subscription.startSubscriptionAction', 'Abonnement starten');
            ornament = {element: <AwesomeIcon name='arrow-right' />, placement: ElementPosition.RIGHT};
        } else {
            btnAction = onClickPortal;
            btnLabel = t('settings.subscription.editSubscription', 'Abonnement wijzigen');
        }
        return (
            <BolmateButton
                onClick={btnAction}
                label={btnLabel}
                secondary={status === SubscriptionStatus.ACTIVE}
                className='subscription-btn'
                ornaments={ornament}
            />
        );
    }

    function renderPriceRow(title: string, price: number, description?: string, extraTop: boolean = false) {
        const style: CSSProperties = {};
        if (extraTop) style.marginTop = '1rem';
        return (
            <div className='price-row' style={style}>
                <div className='price-title-container'>
                    <label className='price-title'>{title}</label>
                    {!!description && <label className='price-description'>{description}</label>}
                </div>
                {renderPrice(price)}
            </div>
        );
    }

    function renderNotificationsModule(price: number) {
        const module = subscription.getModule(SubscriptionModuleName.NOTIFICATIONS);
        const enabled = !!module?.isActive([SubscriptionModuleStatus.CANCELING, SubscriptionModuleStatus.TRIAL]);
        return (
            <div className={classNames('notifications-module', moduleOpen && 'open')}>
                <div className='main-row' onClick={onToggleModuleOpen}>
                    <div className='title-col'>
                        <div className='title-container'>
                            <BolmateSwitch
                                value={enabled}
                                onChange={onToggleNotificationsModule}
                                labels={['', t('settings.subscription.notificationsModule', 'Notificaties module')]}
                                className='notifications-switch common-switch right-label-switch'
                            />
                        </div>
                        <label className='module-description'>
                            {t(
                                'settings.subscription.notificationsDescription',
                                'Krijg 24/7 een melding bij o.a. koopblok verlies en nieuwe reviews'
                            )}{' '}
                            <AwesomeIcon name='chevron-down' className='chev-icon' />
                        </label>
                    </div>
                    <label className='most-picked'>{t('settings.subscription.mostPicked', 'Meest gekozen')}</label>
                    {renderPrice(price, {strike: !enabled})}
                </div>
                <p className='module-info'>
                    {t(
                        'settings.subscription.moduleInfo1',
                        'Met de module Notificaties houd je 24/7 jouw producten in de gaten.'
                    )}
                    <br />
                    {t(
                        'settings.subscription.moduleInfo2',
                        'Vanwege de serverkosten, is er een kleine extra bijdrage.'
                    )}
                    <br />
                    <br />
                    <label className='pro'>
                        <AwesomeIcon name='check' className='check-icon' />{' '}
                        {t('settings.subscription.modulePro1', 'Ontvang belangrijke meldingen direct in jouw inbox.')}
                    </label>
                    <label className='pro'>
                        <AwesomeIcon name='check' className='check-icon' />{' '}
                        {t(
                            'settings.subscription.modulePro2',
                            'Bijvoorbeeld bij een koopblok verlies of een lage voorraad.'
                        )}
                    </label>
                    <label className='pro'>
                        <AwesomeIcon name='check' className='check-icon' />{' '}
                        {t(
                            'settings.subscription.modulePro3',
                            'Zo ben je altijd up-to-date, zodat jij actie kunt ondernemen en geen omzet verlies.'
                        )}
                    </label>
                    <br />
                    {t('settings.subscription.modulePriceInfo', 'Totale prijs met alle verkoopaccounts inbegrepen.')}
                    <br />
                    <br />
                </p>
            </div>
        );
    }

    function renderTotalRow(totalPrice: number) {
        return (
            <div className='price-row total'>
                <div className='pros-container'>
                    <label className='pro gray'>
                        <AwesomeIcon name='check' prefix='fas' className='check-icon' />{' '}
                        {t('settings.subscription.proMoreThan', 'Meer dan {{count}} actieve gebruikers', {
                            count: usersEstimate
                        })}
                    </label>
                    <label className='pro gray'>
                        <AwesomeIcon name='check' prefix='fas' className='check-icon' />{' '}
                        {t('settings.subscription.proStars', '4.9 uit 5 sterren op Google')}
                    </label>
                    <label className='pro gray'>
                        <AwesomeIcon name='check' prefix='fas' className='check-icon' />{' '}
                        {t('settings.subscription.proPartner', 'Officieel partner van bol')}
                    </label>
                </div>
                {renderPrice(discountPrice || totalPrice, {total: true})}
            </div>
        );
    }

    function renderPrice(price: number, config?: {strike?: boolean; total?: boolean}) {
        const strike = config?.strike;
        const total = config?.total;
        return (
            <div className='price-container'>
                <label className={classNames('price', strike && 'strike', total && 'total')}>&euro;{price}</label>
                <label className={classNames('price invisible', strike && 'strike', total && 'total')}>&euro;888</label>
                {total && <label className='month-label'>{t('settings.subscription.perMonth', 'per maand')}</label>}
            </div>
        );
    }

    function renderActionRow() {
        const iconSize = '2rem';
        return (
            <div className='price-row actions'>
                <label className='disclaimer'>
                    {t('settings.subscription.disclaimer1', 'Nooit teveel betalen.')}
                    <br />
                    {t('settings.subscription.disclaimer2', 'Prijs wordt maandelijks bepaald door het aantal orders.')}
                    <br />
                    {t('settings.subscription.disclaimer3', 'Prijzen excl. 21% BTW.')}{' '}
                    <Link route='https://bolmate.nl/#prijzen' target='_blank' className='prices-link'>
                        {t('settings.subscription.allPrices', 'Alle prijzen')}
                    </Link>
                </label>
                <div className='subscription-button-container'>
                    <Sepa width={iconSize} height={iconSize} />
                    <Mastercard width={iconSize} height={iconSize} />
                    <Ideal width={iconSize} height={iconSize} />
                    {renderButton()}
                </div>
            </div>
        );
    }

    return (
        <div className='subscription-block current-details'>
            <div className='header-container'>
                <Image name='logo-no-text' className='logo' />
                <label className='header-title'>
                    {t('settings.subscription.bolmateSubscription', 'Bolmate - Abonnement')}
                </label>
            </div>
            <div className='body-container'>
                <Loader active={loading} />
                <div className='prices-container'>
                    {renderPriceRow(
                        t('settings.subscription.priceForOrders', 'Prijs bij {{count}} orders per maand', {
                            count: orderCount
                        }),
                        currentPricing.orders
                    )}
                    {extraAccounts > 0 &&
                        renderPriceRow(
                            t('settings.subscription.extraAccounts', '{{count}} extra verkoop{{suffix}}', {
                                count: extraAccounts,
                                suffix: renderPlural(extraAccounts, 'account', 'accounts')
                            }),
                            currentPricing.accounts ?? 0
                        )}
                    {renderNotificationsModule(currentPricing.notifications_module ?? 5)}
                    <div className='divider' />
                    {renderTotalRow(currentPricing.total)}
                    {renderActionRow()}
                </div>
            </div>
        </div>
    );
}

CurrentSubscription.displayName = 'CurrentSubscription';
